import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Calendar, CalendarDays, Image as ImageIcon, Settings as SettingsIcon, Pencil, GripVertical, Star, Mail, Ban } from "lucide-react";
import DatePicker from "@/components/DatePicker";
import { ReviewsManager, NewsletterManager, ClosuresManager } from "./ExtraManagers";

const DAYS = ["Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica", "Lunedì"];
const EMPTY_EVENT = { day: "Mercoledì", category: "club", title: "", theme: "", dress_code: "", time: "", info: "", image_url: "" };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { events, gallery, settings, specialDates, closures, refresh } = useSite();
  const navigate = useNavigate();
  const [tab, setTab] = useState("events");

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#0B0A0C] overflow-x-hidden">
      <header className="glass border-b border-[#D4AF37]/15 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div>
            <span className="font-display text-xl gold-text tracking-[0.15em]">AFRODITE</span>
            <span className="ml-3 text-xs text-[#6E6865]">Pannello di Gestione</span>
          </div>
          <button data-testid="admin-logout-btn" onClick={doLogout}
            className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/30 text-[#A39E9B] hover:text-[#FBF8F5] text-sm transition-colors">
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 min-w-0">
        <p className="text-sm text-[#A39E9B] mb-6">Benvenuto, <span className="text-[#D4AF37]">{user?.name || user?.email}</span></p>

        <div className="flex gap-2 mb-8 flex-wrap">
          {[
            { k: "events", label: "Eventi", icon: Calendar },
            { k: "calendar", label: "Calendario", icon: CalendarDays },
            { k: "closures", label: "Chiusure", icon: Ban },
            { k: "gallery", label: "Gallery", icon: ImageIcon },
            { k: "reviews", label: "Recensioni", icon: Star },
            { k: "newsletter", label: "Newsletter", icon: Mail },
            { k: "settings", label: "Contatti & Testi", icon: SettingsIcon },
          ].map((t) => (
            <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm border transition-all ${
                tab === t.k ? "bg-[#7A1426] border-[#D4AF37]/40 text-[#F3E5AB]" : "border-[#D4AF37]/20 text-[#A39E9B] hover:text-[#FBF8F5]"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "events" && <EventsManager events={events} refresh={refresh} />}
        {tab === "calendar" && <CalendarManager specials={specialDates} refresh={refresh} />}
        {tab === "closures" && <ClosuresManager closures={closures} refresh={refresh} />}
        {tab === "gallery" && <GalleryManager gallery={gallery} refresh={refresh} />}
        {tab === "reviews" && <ReviewsManager refresh={refresh} />}
        {tab === "newsletter" && <NewsletterManager />}
        {tab === "settings" && <SettingsManager settings={settings} refresh={refresh} />}
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div className="min-w-0">
      <label className="text-xs text-[#A39E9B] mb-1 block">{label}</label>
      <input {...props} className="w-full px-3 py-2.5 rounded-lg bg-[#0B0A0C] border border-[#D4AF37]/20 text-[#FBF8F5] focus:border-[#D4AF37] outline-none text-sm" />
    </div>
  );
}

function TextArea({ label, value, onChange }) {
  return (
    <div>
      <label className="text-xs text-[#A39E9B] mb-1 block">{label}</label>
      <textarea value={value} onChange={onChange} rows={3}
        className="w-full px-3 py-2.5 rounded-lg bg-[#0B0A0C] border border-[#D4AF37]/20 text-[#FBF8F5] focus:border-[#D4AF37] outline-none text-sm" />
    </div>
  );
}

async function uploadImage(file, category) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", category);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

function EventsManager({ events, refresh }) {
  const [form, setForm] = useState(EMPTY_EVENT);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const edit = (ev) => { setEditId(ev.id); setForm(ev); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const reset = () => { setForm(EMPTY_EVENT); setEditId(null); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { day: form.day, category: form.category, title: form.title, theme: form.theme, dress_code: form.dress_code, time: form.time, info: form.info, image_url: form.image_url };
      if (editId) await api.put(`/events/${editId}`, payload);
      else await api.post("/events", payload);
      toast.success(editId ? "Evento aggiornato" : "Evento creato");
      reset(); await refresh();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo evento?")) return;
    await api.delete(`/events/${id}`); toast.success("Evento eliminato"); await refresh();
  };

  const onFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    try { const url = await uploadImage(file, form.category); setForm((f) => ({ ...f, image_url: url })); toast.success("Immagine caricata"); }
    catch { toast.error("Upload fallito"); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-8 min-w-0">
      <form onSubmit={save} className="lg:col-span-2 min-w-0 space-y-4 p-5 sm:p-6 rounded-2xl border border-[#D4AF37]/15 bg-[#141217] h-fit">
        <h3 className="font-display text-xl text-[#FBF8F5]">{editId ? "Modifica Evento" : "Nuovo Evento"}</h3>
        <div>
          <label className="text-xs text-[#A39E9B] mb-1 block">Sezione</label>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            data-testid="event-category-select"
            className="w-full px-3 py-2.5 rounded-lg bg-[#0B0A0C] border border-[#D4AF37]/20 text-[#FBF8F5] outline-none text-sm">
            <option value="club">Club Privé (serata)</option>
            <option value="spa">Spa Naturista (pomeriggio)</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-[#A39E9B] mb-1 block">Giorno</label>
          <select value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })}
            data-testid="event-day-select"
            className="w-full px-3 py-2.5 rounded-lg bg-[#0B0A0C] border border-[#D4AF37]/20 text-[#FBF8F5] outline-none text-sm">
            {DAYS.map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>
        <Field label="Titolo" data-testid="event-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Field label="Tema" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Dress code" value={form.dress_code} onChange={(e) => setForm({ ...form, dress_code: e.target.value })} />
          <Field label="Orario" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-[#A39E9B] mb-1 block">Descrizione</label>
          <textarea value={form.info} onChange={(e) => setForm({ ...form, info: e.target.value })} rows={3}
            className="w-full px-3 py-2.5 rounded-lg bg-[#0B0A0C] border border-[#D4AF37]/20 text-[#FBF8F5] outline-none text-sm" />
        </div>
        <div>
          <label className="text-xs text-[#A39E9B] mb-1 block">Immagine</label>
          <input type="file" accept="image/*" onChange={onFile} data-testid="event-image-upload" className="text-xs text-[#A39E9B] w-full" />
          {form.image_url && <img src={form.image_url} alt="preview" className="mt-3 h-24 rounded-lg object-cover" />}
        </div>
        <div className="flex gap-3">
          <button type="submit" data-testid="admin-add-event-btn" disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full bg-[#7A1426] hover:bg-[#941B30] text-[#FBF8F5] text-sm font-semibold border border-[#D4AF37]/30 disabled:opacity-60">
            <Plus className="w-4 h-4" /> {editId ? "Aggiorna" : "Aggiungi"}
          </button>
          {editId && <button type="button" onClick={reset} className="px-5 py-3 rounded-full border border-[#D4AF37]/20 text-[#A39E9B] text-sm">Annulla</button>}
        </div>
      </form>

      <div className="lg:col-span-3 min-w-0 space-y-3">
        {events.map((ev) => (
          <div key={ev.id} data-testid={`event-row-${ev.id}`} className="flex items-center gap-4 p-4 rounded-xl border border-[#D4AF37]/12 bg-[#141217]">
            {ev.image_url
              ? <img src={ev.image_url} alt={ev.title} className="w-16 h-16 rounded-lg object-cover shrink-0" />
              : <div className="w-16 h-16 rounded-lg bg-[#0B0A0C] border border-[#D4AF37]/15 shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#D4AF37]">{ev.day}</span>
                <span className={`text-[0.6rem] px-2 py-0.5 rounded-full ${ev.category === "spa" ? "bg-[#C59B6D]/20 text-[#C59B6D]" : "bg-[#7A1426]/30 text-[#F3E5AB]"}`}>
                  {ev.category === "spa" ? "Spa" : "Club"}
                </span>
              </div>
              <p className="font-display text-lg text-[#FBF8F5] truncate">{ev.title}</p>
              <p className="text-xs text-[#6E6865] truncate">{ev.theme} · {ev.time}</p>
            </div>
            <button onClick={() => edit(ev)} className="p-2 text-[#A39E9B] hover:text-[#D4AF37]"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => del(ev.id)} data-testid={`event-delete-${ev.id}`} className="p-2 text-[#A39E9B] hover:text-[#941B30]"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function GalleryManager({ gallery, refresh }) {
  const [category, setCategory] = useState("club");
  const [uploading, setUploading] = useState(false);
  const [list, setList] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  useEffect(() => {
    setList(gallery.filter((g) => g.category === category));
  }, [gallery, category]);

  const onFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file, category);
      await api.post("/gallery", { category, image_url: url, caption: "" });
      toast.success("Immagine aggiunta"); await refresh();
    } catch { toast.error("Upload fallito"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const del = async (id) => { await api.delete(`/gallery/${id}`); toast.success("Rimossa"); await refresh(); };
  const saveCaption = async (id, caption) => { await api.put(`/gallery/${id}`, { caption }); toast.success("Didascalia salvata"); await refresh(); };

  const onDrop = async (i) => {
    const from = dragIndex;
    setDragIndex(null); setOverIndex(null);
    if (from === null || from === i) return;
    const nl = [...list];
    const [moved] = nl.splice(from, 1);
    nl.splice(i, 0, moved);
    setList(nl);
    try { await api.post("/gallery/reorder", { ids: nl.map((x) => x.id) }); toast.success("Ordine aggiornato"); await refresh(); }
    catch { toast.error("Errore nel riordino"); await refresh(); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6 p-5 rounded-2xl border border-[#D4AF37]/15 bg-[#141217]">
        <span className="text-sm text-[#A39E9B]">Sezione:</span>
        {["club", "spa"].map((c) => (
          <button key={c} onClick={() => setCategory(c)} data-testid={`gallery-cat-${c}`}
            className={`px-4 py-2 rounded-full text-sm border ${category === c ? "bg-[#7A1426] border-[#D4AF37]/40 text-[#F3E5AB]" : "border-[#D4AF37]/20 text-[#A39E9B]"}`}>
            {c === "club" ? "Club Privé" : "Spa Naturista"}
          </button>
        ))}
        <label data-testid="gallery-upload-label" className="ml-auto cursor-pointer flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4AF37] text-black text-sm font-semibold hover:bg-[#F3E5AB] transition-colors">
          <Plus className="w-4 h-4" /> {uploading ? "Caricamento..." : "Carica immagine"}
          <input type="file" accept="image/*" onChange={onFile} disabled={uploading} className="hidden" />
        </label>
      </div>

      <p className="text-xs text-[#6E6865] mb-4 flex items-center gap-2"><GripVertical className="w-3.5 h-3.5" /> Trascina le immagini per cambiarne l'ordine di visualizzazione sul sito.</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {list.map((it, i) => (
          <div
            key={it.id}
            draggable
            onDragStart={() => setDragIndex(i)}
            onDragOver={(e) => { e.preventDefault(); setOverIndex(i); }}
            onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
            onDrop={() => onDrop(i)}
            data-testid={`gallery-drag-${it.id}`}
            className={`transition-transform duration-200 ${dragIndex === i ? "opacity-40" : ""} ${overIndex === i && dragIndex !== i ? "ring-2 ring-[#D4AF37] rounded-xl" : ""}`}
          >
            <GalleryCard item={it} onDelete={del} onSaveCaption={saveCaption} />
          </div>
        ))}
      </div>
    </div>
  );
}

function GalleryCard({ item, onDelete, onSaveCaption }) {
  const [caption, setCaption] = useState(item.caption || "");
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); try { await onSaveCaption(item.id, caption); } finally { setSaving(false); } };
  const dirty = caption !== (item.caption || "");
  return (
    <div className="rounded-xl overflow-hidden border border-[#D4AF37]/12 bg-[#141217]">
      <div className="relative group">
        <img src={item.image_url} alt={item.caption} className="w-full h-40 object-cover" />
        <button onClick={() => onDelete(item.id)} data-testid={`gallery-delete-${item.id}`}
          className="absolute top-2 right-2 p-2 rounded-full bg-black/70 text-[#FBF8F5] opacity-0 group-hover:opacity-100 hover:bg-[#941B30] transition-all">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="p-2 flex gap-2">
        <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Didascalia elegante..."
          data-testid={`gallery-caption-${item.id}`}
          className="flex-1 min-w-0 px-2 py-1.5 rounded bg-[#0B0A0C] border border-[#D4AF37]/20 text-[#FBF8F5] text-xs outline-none focus:border-[#D4AF37]" />
        <button onClick={save} disabled={saving || !dirty} data-testid={`gallery-caption-save-${item.id}`}
          className="px-3 py-1.5 rounded bg-[#7A1426] text-[#F3E5AB] text-xs font-semibold disabled:opacity-40 hover:bg-[#941B30] transition-colors">Salva</button>
      </div>
    </div>
  );
}

function CalendarManager({ specials, refresh }) {
  const EMPTY = { date: "", category: "club", title: "", description: "", image_url: "" };
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const reset = () => { setForm(EMPTY); setEditId(null); };
  const edit = (s) => { setEditId(s.id); setForm({ date: s.date, category: s.category, title: s.title, description: s.description || "", image_url: s.image_url || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    if (!form.date) { toast.error("Scegli una data"); setSaving(false); return; }
    try {
      if (editId) await api.put(`/special-dates/${editId}`, form);
      else await api.post("/special-dates", form);
      toast.success(editId ? "Data aggiornata" : "Data aggiunta");
      reset(); await refresh();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare questa data?")) return; await api.delete(`/special-dates/${id}`); toast.success("Eliminata"); await refresh(); };
  const onFile = async (e) => { const f = e.target.files[0]; if (!f) return; try { const url = await uploadImage(f, form.category); setForm((p) => ({ ...p, image_url: url })); toast.success("Immagine caricata"); } catch { toast.error("Upload fallito"); } };
  const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div className="grid lg:grid-cols-5 gap-8 min-w-0">
      <form onSubmit={save} className="lg:col-span-2 min-w-0 space-y-4 p-5 sm:p-6 rounded-2xl border border-[#D4AF37]/15 bg-[#141217] h-fit">
        <h3 className="font-display text-xl text-[#FBF8F5]">{editId ? "Modifica Data" : "Nuova Data Speciale"}</h3>
        <div>
          <label className="text-xs text-[#A39E9B] mb-1 block">Data</label>
          <DatePicker value={form.date} onChange={(d) => setForm({ ...form, date: d })} testid="special-date-input" />
        </div>
        <div>
          <label className="text-xs text-[#A39E9B] mb-1 block">Sezione</label>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="special-category-select"
            className="w-full px-3 py-2.5 rounded-lg bg-[#0B0A0C] border border-[#D4AF37]/20 text-[#FBF8F5] outline-none text-sm">
            <option value="club">Club Privé</option>
            <option value="spa">Spa Naturista</option>
          </select>
        </div>
        <Field label="Titolo" data-testid="special-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <div>
          <label className="text-xs text-[#A39E9B] mb-1 block">Descrizione</label>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
            className="w-full px-3 py-2.5 rounded-lg bg-[#0B0A0C] border border-[#D4AF37]/20 text-[#FBF8F5] outline-none text-sm" />
        </div>
        <div>
          <label className="text-xs text-[#A39E9B] mb-1 block">Locandina dell'evento</label>
          <input type="file" accept="image/*" onChange={onFile} data-testid="special-image-upload" className="text-xs text-[#A39E9B] w-full" />
          <p className="text-[0.65rem] text-[#6E6865] mt-1">Formato verticale consigliato (come le locandine delle serate). Apparirà nel dettaglio del calendario.</p>
          {form.image_url && <img src={form.image_url} alt="preview" className="mt-3 h-40 rounded-lg object-cover object-top" />}
        </div>
        <div className="flex gap-3">
          <button type="submit" data-testid="special-add-btn" disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full bg-[#7A1426] hover:bg-[#941B30] text-[#FBF8F5] text-sm font-semibold border border-[#D4AF37]/30 disabled:opacity-60">
            <Plus className="w-4 h-4" /> {editId ? "Aggiorna" : "Aggiungi"}
          </button>
          {editId && <button type="button" onClick={reset} className="px-5 py-3 rounded-full border border-[#D4AF37]/20 text-[#A39E9B] text-sm">Annulla</button>}
        </div>
      </form>

      <div className="lg:col-span-3 min-w-0 space-y-3">
        {specials.length === 0 && <p className="text-sm text-[#6E6865]">Nessuna data speciale. Aggiungine una dal modulo.</p>}
        {specials.map((s) => (
          <div key={s.id} data-testid={`special-row-${s.id}`} className="flex items-center gap-4 p-4 rounded-xl border border-[#D4AF37]/12 bg-[#141217]">
            {s.image_url
              ? <img src={s.image_url} alt={s.title} className="w-16 h-16 rounded-lg object-cover shrink-0" />
              : <div className="w-16 h-16 rounded-lg bg-[#0B0A0C] border border-[#D4AF37]/15 shrink-0 flex items-center justify-center"><CalendarDays className="w-6 h-6 text-[#D4AF37]/50" /></div>}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#D4AF37]">{fmt(s.date)}</span>
                <span className={`text-[0.6rem] px-2 py-0.5 rounded-full ${s.category === "spa" ? "bg-[#C59B6D]/20 text-[#C59B6D]" : "bg-[#7A1426]/30 text-[#F3E5AB]"}`}>{s.category === "spa" ? "Spa" : "Club"}</span>
              </div>
              <p className="font-display text-lg text-[#FBF8F5] truncate">{s.title}</p>
              <p className="text-xs text-[#6E6865] truncate">{s.description}</p>
            </div>
            <button onClick={() => edit(s)} className="p-2 text-[#A39E9B] hover:text-[#D4AF37]"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => del(s.id)} data-testid={`special-delete-${s.id}`} className="p-2 text-[#A39E9B] hover:text-[#941B30]"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsManager({ settings, refresh }) {
  const [form, setForm] = useState({
    phone: settings.phone || "", whatsapp: settings.whatsapp || "", facebook_url: settings.facebook_url || "",
    instagram_url: settings.instagram_url || "", telegram: settings.telegram || "", music_url: settings.music_url || "",
    address: settings.address || "", opening_hours: settings.opening_hours || "", announcement: settings.announcement || "",
    club_intro: settings.club_intro || "", spa_intro: settings.spa_intro || "", about_text: settings.about_text || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const onMusic = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("File troppo grande (max 15 MB)"); return; }
    setUploadingMusic(true);
    try { const url = await uploadImage(f, "music"); setForm((p) => ({ ...p, music_url: url })); toast.success("Brano caricato: ricorda di salvare"); }
    catch { toast.error("Upload fallito"); }
    finally { setUploadingMusic(false); e.target.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.put("/settings", form); toast.success("Impostazioni salvate"); await refresh(); }
    catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <form onSubmit={save} className="max-w-2xl min-w-0 space-y-5 p-5 sm:p-6 rounded-2xl border border-[#D4AF37]/15 bg-[#141217]">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Telefono" value={form.phone} onChange={set("phone")} />
        <Field label="WhatsApp (es. 393331512490)" value={form.whatsapp} onChange={set("whatsapp")} />
      </div>
      <Field label="Link Facebook" value={form.facebook_url} onChange={set("facebook_url")} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Instagram (URL)" value={form.instagram_url} onChange={set("instagram_url")} />
        <Field label="Telegram (numero es. 393331512490)" value={form.telegram} onChange={set("telegram")} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Indirizzo / Zona" value={form.address} onChange={set("address")} />
        <Field label="Orari di apertura" value={form.opening_hours} onChange={set("opening_hours")} />
      </div>
      <TextArea label="Annuncio (banner contatti)" value={form.announcement} onChange={set("announcement")} />
      <TextArea label="Introduzione Club Privé" value={form.club_intro} onChange={set("club_intro")} />
      <TextArea label="Introduzione Spa Naturista" value={form.spa_intro} onChange={set("spa_intro")} />
      <TextArea label="Testo Chi Siamo / Statuto" value={form.about_text} onChange={set("about_text")} />
      <div className="p-4 rounded-xl border border-[#D4AF37]/15 bg-[#0B0A0C]">
        <label className="text-xs text-[#A39E9B] mb-2 block">Musica di sottofondo (MP3, max 15 MB)</label>
        <p className="text-[0.65rem] text-[#6E6865] mb-3">Se non carichi nulla viene usata la melodia lounge generata automaticamente.</p>
        <input type="file" accept="audio/mpeg,audio/mp3,audio/mp4,audio/ogg,audio/wav,.mp3,.m4a,.ogg,.wav" onChange={onMusic} disabled={uploadingMusic} data-testid="music-upload-input" className="text-xs text-[#A39E9B] w-full" />
        {uploadingMusic && <p className="text-xs text-[#D4AF37] mt-2">Caricamento...</p>}
        {form.music_url && (
          <div className="mt-3 flex items-center gap-3">
            <audio controls src={form.music_url} data-testid="music-preview" className="h-8 flex-1" />
            <button type="button" onClick={() => setForm((p) => ({ ...p, music_url: "" }))} data-testid="music-remove-btn"
              className="text-xs text-[#A39E9B] hover:text-[#941B30]">Rimuovi</button>
          </div>
        )}
      </div>
      <button type="submit" data-testid="admin-save-settings-btn" disabled={saving}
        className="w-full py-3.5 rounded-full bg-[#7A1426] hover:bg-[#941B30] text-[#FBF8F5] font-semibold border border-[#D4AF37]/30 disabled:opacity-60">
        {saving ? "Salvataggio..." : "Salva Modifiche"}
      </button>
    </form>
  );
}
